import { z } from 'zod';
import { isoDateSchema, round2 } from './common.ts';
import { METHODES_PAIEMENT, type MethodePaiement } from './paiement.ts';

/**
 * Clôture de caisse — ce qui est entré dans le tiroir un jour donné.
 *
 * Deux sources, une seule journée : les ventes au comptoir, réglées sur place,
 * et les paiements reçus sur des commandes. C'est la réunion des deux qu'on
 * compare au tiroir le soir ; n'en prendre qu'une donnerait un écart tous les
 * jours où quelqu'un vient verser une avance.
 *
 * Rien n'est stocké. La clôture est une lecture de la journée, pas un document
 * : la source de vérité reste les ventes et les paiements eux-mêmes, et une
 * correction faite le lendemain se répercute sur la clôture réimprimée.
 */

export const ORIGINES_ENCAISSEMENT = ['COMPTOIR', 'COMMANDE'] as const;
export type OrigineEncaissement = (typeof ORIGINES_ENCAISSEMENT)[number];

export const ORIGINE_ENCAISSEMENT_LABELS: Record<OrigineEncaissement, string> = {
  COMPTOIR: 'Vente au comptoir',
  COMMANDE: 'Paiement sur commande',
};

export const caisseQuerySchema = z.object({
  /** Jour à clôturer. Par défaut : aujourd'hui, décidé côté serveur. */
  date: isoDateSchema.optional(),
});
export type CaisseQuery = z.infer<typeof caisseQuerySchema>;

/** Une entrée (ou une sortie) de caisse de la journée. */
export type LigneCaisse = {
  origine: OrigineEncaissement;
  /** Identifiant de la vente ou du paiement, pour retrouver la pièce. */
  id: string;
  /** Vers quoi pointer : la vente, ou la commande réglée. */
  cible_id: string;
  reference: string | null;
  libelle: string;
  methode: MethodePaiement | null;
  /** Négatif pour un remboursement (règle 9). */
  montant: number;
  heure: string | null;
};

export type TotalMethode = {
  methode: MethodePaiement | null;
  montant: number;
  operations: number;
};

export type Caisse = {
  date: string;
  /** Un poste par méthode rencontrée, plus les montants sans méthode. */
  par_methode: TotalMethode[];
  total: number;
  /** Ce qui doit se trouver physiquement dans le tiroir. */
  especes: number;
  operations: number;
  lignes: LigneCaisse[];
};

/**
 * Regroupe les lignes par méthode de règlement.
 *
 * Toutes les méthodes connues sont rendues, même à zéro : une ligne « Chèque —
 * 0,00 DH » dit qu'il n'y a pas eu de chèque, tandis qu'une ligne absente
 * laisse penser qu'on a oublié de regarder. Les montants sans méthode sont
 * regroupés à part plutôt que rangés dans « Autre », qui est un choix explicite
 * de l'opérateur et ne doit pas se confondre avec l'absence de choix.
 */
export function totauxParMethode(lignes: readonly LigneCaisse[]): TotalMethode[] {
  const par = new Map<MethodePaiement | null, TotalMethode>();
  for (const m of METHODES_PAIEMENT) par.set(m, { methode: m, montant: 0, operations: 0 });

  for (const l of lignes) {
    const cle = l.methode ?? null;
    const poste = par.get(cle) ?? { methode: cle, montant: 0, operations: 0 };
    poste.montant = round2(poste.montant + l.montant);
    poste.operations += 1;
    par.set(cle, poste);
  }

  // Le poste « sans méthode » ne s'affiche que s'il a servi.
  return [...par.values()].filter((p) => p.methode !== null || p.operations > 0);
}

/** Total de la journée, remboursements déduits. */
export function totalCaisse(lignes: readonly LigneCaisse[]): number {
  return round2(lignes.reduce((acc, l) => acc + l.montant, 0));
}

/** Ce qui doit se trouver dans le tiroir : les espèces, et elles seules. */
export function especesCaisse(lignes: readonly LigneCaisse[]): number {
  return round2(
    lignes.reduce((acc, l) => (l.methode === 'ESPECES' ? acc + l.montant : acc), 0),
  );
}

/**
 * Écart entre le tiroir compté et ce que l'application attend.
 *
 * Positif : il y a plus dans le tiroir que prévu. Négatif : il en manque.
 * Rendu tel quel, sans valeur absolue — les deux sens ne se corrigent pas de
 * la même façon.
 */
export function ecartCaisse(compte: number, attendu: number): number {
  return round2(compte - attendu);
}
